/**
 * LER o estado dos conectores da etapa 3 FORA de um turno.
 *
 * ★ **Não existe endpoint do onboarding para isto.** `estado_dos_conectores`
 * (`core/onboarding/conectores.py`) tem UM chamador — o usecase do chat, dentro
 * do turno — e nenhuma rota. O que existe são as leituras que as telas de CRM e
 * de Atendimento já usam, e este arquivo as compõe no vocabulário do cartão.
 *
 * ⚠️ **Isto é uma SEGUNDA implementação da régua do backend**, e o custo está
 * declarado no fim deste cabeçalho. O conserto de raiz é o backend publicar
 * `GET /onboarding/conectores` devolvendo `estado_dos_conectores(...)`.
 *
 * | conector | como se lê aqui | espelha |
 * |---|---|---|
 * | `email_agenda` | `getStatus()` do Google E da Microsoft; ligado = qualquer `autenticado` | `_tem_oauth_de_agenda` (`integracao_oauth_token`, provedor em `PROVEDORES_DE_EMAIL_AGENDA`) |
 * | `whatsapp` | `listarConexoes()`; ligado = alguma conexão com `status='conectado'` **e** cujo `profissionais[]` contém o profissional logado | `_tem_conexao_de_whatsapp` (vínculo `ativo` + `atendimento_conexao.status='conectado'`) |
 *
 * ⚠️ **O `profissionais[]` é obrigatório no predicado do WhatsApp, não zelo.**
 * `listar_conexoes` devolve ao GESTOR todas as conexões ativas do escritório,
 * vinculado ou não — contar a LISTA diria "conectado" para um gestor que nunca
 * pareou nada. O backend pergunta pelo VÍNCULO, e é o vínculo que vem naquele
 * campo (`_enrich_with_professionals`, filtrado por `ativo`).
 *
 * ★ **Só o APURADO é devolvido.** Leitura que falhou omite a chave — nunca
 * escreve `desconhecido` por cima. Desfazer um `conectado` por causa de uma
 * queda de rede seria desapurar um fato; a mesma disciplina do `null ≠ 0` da
 * faixa de sugestões.
 *
 * ⚠️ **Divergência conhecida:** aqui uma conexão desativada some da lista, e no
 * backend um vínculo órfão (`ativo`) apontando para ela ainda diria `conectado`.
 * Esta leitura é a mais estrita, e é a que bate com o que a tela de Atendimento
 * mostra.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import { apiCrmGoogleIntegration } from '@modules/crm/api/apiCrmGoogleIntegration'
import { apiCrmMicrosoftIntegration } from '@modules/crm/api/apiCrmMicrosoftIntegration'
import { apiAtendimentoConexao } from '@modules/settings/api/apiAtendimentoConexao'
import {
  CONECTOR_EMAIL_AGENDA,
  CONECTOR_WHATSAPP,
  ESTADO_CONECTADO,
  ESTADO_DESCONECTADO,
} from '@modules/aawz-intelligence-v2/constants/Conectores'

const estadoDe = (ligado) => (ligado ? ESTADO_CONECTADO : ESTADO_DESCONECTADO)

/**
 * O único `status` de `atendimento_conexao` que significa PAREADO.
 *
 * ⚠️ Espelho MANUAL de `core/onboarding/conectores.py::STATUS_CONEXAO_CONECTADA`.
 * O vocabulário da tabela é `conectando | conectado | desconectado`, e os dois
 * primeiros convivem com `numero_whatsapp` VAZIO.
 *
 * ⚠️ **Não é o `ESTADO_CONECTADO` do conector**, apesar do mesmo literal: um é
 * o status da CONEXÃO (tabela), o outro é o estado do CONECTOR no cartão.
 * Colapsar os dois faria o renome de um arrastar o outro.
 */
const STATUS_CONEXAO_CONECTADA = 'conectado'

const respostaApurada = (r) => Boolean(r) && r.status === 200 && Boolean(r.value)

/**
 * E-mail/agenda está ligado? `null` quando NENHUM dos dois provedores respondeu.
 *
 * ⚠️ Um provedor apurado basta para dizer `conectado`; para dizer `desconectado`
 * os dois precisam ter respondido — senão a Microsoft caída apagaria o marcador
 * de quem autorizou por ela.
 *
 * @param {Array} respostas `[respostaGoogle, respostaMicrosoft]`
 * @returns {?String}
 */
export function estadoDoEmailAgenda(respostas) {
  const lista = Array.isArray(respostas) ? respostas : []
  const apuradas = lista.filter(respostaApurada)
  if (!apuradas.length) return null
  const algumLigado = apuradas.some((r) => r.value.autenticado === true)
  if (algumLigado) return ESTADO_CONECTADO
  if (apuradas.length < lista.length) return null
  return ESTADO_DESCONECTADO
}

/**
 * O WhatsApp está ligado PARA ESTE profissional? `null` quando a leitura falhou
 * ou quando não se sabe quem é o profissional.
 *
 * ⚠️ **O piso é `> 0`, e não `Boolean(id)`.** `user.id_interno` nasce **`-1`**
 * (`state/modules/user/state.js`) e só vira o id de verdade quando a sessão
 * carrega — `-1` é truthy, casaria com ninguém e o cartão diria `desconectado`
 * a partir de um id que não é de ninguém. "Ainda não sei quem é" tem de sair
 * como não apurado.
 *
 * @param {Object} resposta O envelope de `apiAtendimentoConexao.listarConexoes`.
 * @param {Number} idProfissional `$currentUser().id_interno`.
 * @returns {?String}
 */
export function estadoDoWhatsapp(resposta, idProfissional) {
  if (!respostaApurada(resposta)) return null
  if (!(idProfissional > 0)) return null
  const conexoes = Array.isArray(resposta.value) ? resposta.value : []
  const pareado = conexoes.some((conexao) => {
    // ⚠️⚠️ **O VÍNCULO ATIVO NÃO É CONEXÃO** — e esta metade FALTAVA aqui
    // (20/09/2026). `atendimento_conexao_profissional` nasce no clique em
    // "Criar conexão", ANTES de o QR ser lido; quem guarda se o pareamento
    // aconteceu é o `status` da conexão. Sem esta linha, a reconferência
    // repintava o cartão para "Conectado" assim que a conexão era CRIADA —
    // reabrindo, pela porta do front, exatamente o defeito que o owner já
    // pegou no backend (*"por que meu WhatsApp está dizendo que está
    // conectado sendo que não conectei?"*, 19/09/2026), que
    // `_tem_conexao_de_whatsapp` fechou exigindo `status='conectado'`.
    if (!conexao || conexao.status !== STATUS_CONEXAO_CONECTADA) return false
    const profissionais = conexao.profissionais || []
    return profissionais.some((p) => p && p.id_profissional === idProfissional)
  })
  return estadoDe(pareado)
}

/**
 * Lê os dois conectores em paralelo e devolve SÓ o que foi apurado.
 *
 * ⚠️ Nenhuma falha vira snack: é releitura de fundo que ninguém pediu, e o
 * cartão continua mostrando o retrato do turno — que é a fonte honesta quando
 * não há leitura nova.
 *
 * @param {Object} opcoes
 * @param {Number} opcoes.idProfissional
 * @returns {Promise<Object>} `{ [id do conector]: estado }` — só os apurados.
 * @example await lerEstadoDosConectores({ idProfissional: 895 })
 */
export async function lerEstadoDosConectores({ idProfissional } = {}) {
  const respostas = await Promise.all([
    apiCrmGoogleIntegration.getStatus(),
    apiCrmMicrosoftIntegration.getStatus(),
    apiAtendimentoConexao.listarConexoes(),
  ])
  const estados = {}
  const emailAgenda = estadoDoEmailAgenda([respostas[0], respostas[1]])
  if (emailAgenda) estados[CONECTOR_EMAIL_AGENDA] = emailAgenda
  const whatsapp = estadoDoWhatsapp(respostas[2], idProfissional)
  if (whatsapp) estados[CONECTOR_WHATSAPP] = whatsapp
  return estados
}
